import { PhoneNumberUtil } from 'google-libphonenumber'
import { UserPlus } from 'lucide-react'
import { useState } from 'react'
import {
  GetActiveUsersDocument,
  GetActiveUsersQuery,
  useCreateUserMutation,
  useGetOrganizationRolesQuery,
} from '~/common/generated/apollo-operations'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { DossLogo } from '../navigation/DossLogo'
import { AddUserDialog, UserFormData } from '../settings/AddUserDialog'
import { useDossSnackbar } from '../shared/DossSnackbar'
import { HelpPopover } from './HelpPopover'
import { LeftNavAccountMenu } from './LeftNavAccountMenu'

export const LeftNavFooter = () => {
  const { t } = useTypedTranslation()
  const { user, isAdmin, isInternal } = useGlobalContext()
  const [openAddUserDialog, setOpenAddUserDialog] = useState(false)
  const { data: rolesData } = useGetOrganizationRolesQuery({ skip: !openAddUserDialog })
  const snackbar = useDossSnackbar()

  const [createUser] = useCreateUserMutation({
    update(cache, { data }) {
      if (!data) {
        return
      }

      const queryData: GetActiveUsersQuery | null = cache.readQuery({
        query: GetActiveUsersDocument,
      })

      if (queryData) {
        cache.writeQuery({
          query: GetActiveUsersDocument,
          data: {
            activeUsers: [...queryData.activeUsers, data.createUser],
          },
        })
      }
    },
  })

  const handleAddUser = async (formData: UserFormData) => {
    if (!user) {
      return
    }

    if (!formData.email && !formData.phoneNumber) {
      snackbar.showError(t('user.add_user_dialog.email_phone_required'))
      return
    }

    let phoneNumber: string | undefined = formData.phoneNumber
    if (phoneNumber) {
      const phoneUtil = PhoneNumberUtil.getInstance()
      try {
        phoneNumber = `+${formData.phoneNumber}`
        const parsedPhoneNumber = phoneUtil.parse(phoneNumber)
        if (!phoneUtil.isValidNumber(parsedPhoneNumber)) {
          snackbar.showError(t('user.add_user_dialog.invalid_phone_number'))
          return
        }
      } catch {
        snackbar.showError(t('user.add_user_dialog.invalid_phone_number'))
        return
      }
    } else {
      phoneNumber = undefined
    }

    try {
      await createUser({
        variables: {
          input: {
            ...formData,
            phoneNumber,
            orientation: user.orientation,
          },
        },
      })
      setOpenAddUserDialog(false)
      snackbar.showSuccess(t('user.users_table.add_user_success'))
    } catch (error) {
      const errorMessage = (error as Error).message || t('user.users_table.add_user_error')
      snackbar.showError(errorMessage)
    }
  }

  if (!user) {
    return null
  }

  return (
    <div className="flex shrink-0 flex-col border-t border-gray-4">
      {/* Account Settings Row */}
      <div className="p-2">
        <LeftNavAccountMenu />

        {/* Invite People Row - Only for admin/internal users */}
        {(isAdmin || isInternal) && (
          <div
            className="flex cursor-pointer items-center gap-2 rounded px-3 py-2 hover:bg-gray-2"
            onClick={() => setOpenAddUserDialog(true)}
          >
            <div className="flex size-6 shrink-0 items-center justify-center">
              <UserPlus className="text-gray-10" size={18} />
            </div>
            <span className="min-w-0 truncate text-sm text-gray-10">
              {t('left_nav.invite_people')}
            </span>
          </div>
        )}
      </div>

      {/* Version & Help Row */}
      <div className="flex items-center justify-between border-t border-gray-4 px-3 py-2">
        <div className="flex min-w-0 items-center gap-2">
          <DossLogo height={12} hoverable={false} variant="dark" width={12} />
          {user.organizationVersion && (
            <span className="truncate text-xs text-gray-10">
              version {user.organizationVersion}
            </span>
          )}
        </div>
        <HelpPopover />
      </div>

      {/* Add User Dialog */}
      {openAddUserDialog && (
        <AddUserDialog
          onClose={() => setOpenAddUserDialog(false)}
          onSubmit={handleAddUser}
          roles={rolesData?.roles ?? []}
        />
      )}
    </div>
  )
}
